import { translate } from '@chaika/i18n/helper';
import { translationDataService } from '@store/Configuration/translationDataService';
import React, { Suspense } from 'react';
import { observer, inject } from 'mobx-react';
import { RouteProps } from 'react-router';
import { withRouter, WithRouterProps } from '@hoc/withRouterProps';
import autobind from 'autobind';
import isEmpty from 'lodash/isEmpty';
import { WithTranslation, withTranslation } from 'react-i18next';

import { AuthUser } from '@store/AuthUser/AuthUser';
import { isWalkInEhrRoute } from '@modules/walkIn/routes/walkInRoutes';
import { authUserService } from '@store/AuthUser/services/authUserService';

import { AuthApi } from '@api/AuthApi';

import { PageProps } from '@core/Page';
import { PageRouterProps } from '@core/Router/PageRouter';
import { organizationService } from '@organization/service/organizaion/organizationService';
import { featureToggleService } from '@store/Configuration/featureToggleService';
import { appService } from '@store/App/appService';
import { legalEntityBranchService } from '@organization/service/legalEntityBranch/legalEntityBranchService';
import { AppState } from '@store/App/App';

interface Props extends PageRouterProps {
    Component: React.ComponentType<PageProps>;
}

interface StoreProps {
    app: AppState;
    authUser: AuthUser;
}

interface State {
    isLoaded: boolean;
}

const injectableStores: (keyof StoreProps)[] = [AuthUser.Name, AppState.Name];

@(withRouter as any)
@observer
class Component extends React.Component<
    Props & StoreProps & WithRouterProps & Omit<RouteProps, 'Component'> & WithTranslation,
    State
> {
    public state: State = {
        isLoaded: false,
    };

    public render(): React.ReactNode {
        return this.renderContent();
    }

    public async componentDidMount(): Promise<void> {
        const { authUser, i18n, navigate } = this.props;

        const authUserLoaded = authUser.isAuthorized;
        const authUserData = !authUserLoaded
            ? await authUserService.getAuthUserData().catch(() => undefined)
            : undefined;
        await Promise.all([
            !authUserLoaded && this.updateFromUrl(),
            !!authUserData?.id &&
                authUserService
                    .loadAuthorizedUserData(authUserData, this.props.location?.pathname.startsWith('/schedule'))
                    .catch(() => false),
            authUser.isAuthorized && organizationService.loadByIds(authUser.organizationIds),
            authUser.isAuthorized && featureToggleService.load(),
            authUser.isAuthorized && appService.loadServicesConfig(),
            authUser.isAuthorized && appService.loadSupportConfig(),
            authUser.isAuthorized && legalEntityBranchService.load(),
            authUser.isAuthorized && translationDataService.load(),
        ]);

        authUser.initLocale();
        i18n.changeLanguage(this.props.authUser.selectedLanguage);

        const userOrganizationIds = authUserService.getUserOrganizationIds();
        const isAutoSelectOrganization =
            !(authUser.isDirector || authUser.isAssistant) &&
            !authUser.organizationId &&
            userOrganizationIds.length === 1;

        if (isAutoSelectOrganization) {
            authUserService.updateSelectedOrganization(userOrganizationIds[0]);
        }

        if (authUser.isOnlyAccountant) {
            navigate('/accounting');
        }

        if (!authUser.hasLaboratoryDiagnosticsAccess) {
            this.redirectFromWalkInEhr();
        }

        this.setState({ isLoaded: true });
    }

    private redirectFromWalkInEhr(): void {
        const { navigate, location } = this.props;

        if (isWalkInEhrRoute(location?.pathname ?? '')) {
            navigate('/dashboard?currentPage=timeLine');
        }
    }

    @autobind
    private renderContent(): React.ReactNode {
        const { authUser, location } = this.props;
        let content: React.ReactNode;

        if (this.state.isLoaded) {
            if (location.pathname !== '/login' && !authUser.isAuthorized) {
                this.updateFromUrl();
                this.redirectToLogin();
            } else {
                content = this.getPageContent();
            }
        }

        return content;
    }

    private getPageContent(): React.ReactNode {
        const { Component } = this.props;

        return (
            <Suspense fallback={<div>{translate('loading')}</div>}>
                {React.createElement(Component, this.props)}
            </Suspense>
        );
    }

    private async redirectToLogin(): Promise<void> {
        await AuthApi.redirectToLogin();
    }

    private async updateFromUrl(): Promise<void> {
        let from: string = '/';
        if (!isEmpty(this.props.location.pathname)) {
            from = this.props.location.pathname;
            if (!isEmpty(this.props.location.search)) {
                from = `${from}?${this.props.location.search}`;
            }
        }

        if (!from.includes('login')) {
            await AuthApi.setFromUrl(encodeURIComponent(from));
        }
    }
}

export const RouteContainer = withTranslation()(inject<Props, StoreProps>(...injectableStores)(Component));
